import { usePortal } from "@booking/hooks/usePortal";
import React, { JSX } from "react";

import useScrollLock from "@/hooks/useScrollLock";

import CreateYourOwnErrorView from "./CreateYourOwnErrorView";
import styles from "./CreateYourOwnModal.module.css";
import { CreateYourOwnPersonalFormData } from "./CreateYourOwnPersonalForm";
import CreateYourOwnSuccessView from "./CreateYourOwnSuccessView";
import { CreateYourOwnTravelFormData } from "./CreateYourOwnTravelForm";

interface CreateYourOwnModalProps {
  isOpen: boolean;
  modalState: "success" | "error" | null;
  travelDetails: CreateYourOwnTravelFormData | null;
  personalDetails: CreateYourOwnPersonalFormData | null;
  venueName?: string;
  setModalState: React.Dispatch<
    React.SetStateAction<"success" | "error" | null>
  >;
  setTravelDetails: React.Dispatch<
    React.SetStateAction<CreateYourOwnTravelFormData | null>
  >;
  setPersonalDetails: React.Dispatch<
    React.SetStateAction<CreateYourOwnPersonalFormData | null>
  >;
  setStep: React.Dispatch<React.SetStateAction<"travel" | "personal">>;
}

enum ModalState {
  SUCCESS = "success",
  ERROR = "error",
}

function CreateYourOwnModal(props: CreateYourOwnModalProps) {
  const Portal = usePortal();
  const {
    isOpen,
    modalState,
    travelDetails,
    personalDetails,
    venueName = "",
    setModalState,
    setTravelDetails,
    setPersonalDetails,
    setStep,
  } = props;

  useScrollLock(isOpen);

  const handleCloseSuccessModal = () => {
    setModalState(null);
    setTravelDetails(null);
    setPersonalDetails(null);
    setStep("travel");
  };

  const handleCloseErrorModal = () => {
    setModalState(null);
  };

  const handleOverlayClick = () => {
    if (modalState === ModalState.SUCCESS) {
      handleCloseSuccessModal();
    } else {
      handleCloseErrorModal();
    }
  };

  const contentMap: Record<ModalState, JSX.Element> = {
    [ModalState.SUCCESS]: (
      <CreateYourOwnSuccessView
        travelDetails={travelDetails}
        personalDetails={personalDetails}
        venueName={venueName}
        onClose={handleCloseSuccessModal}
      />
    ),
    [ModalState.ERROR]: (
      <CreateYourOwnErrorView onRetry={handleCloseErrorModal} />
    ),
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <div
        className={styles.modal}
        aria-modal="true"
        role="dialog"
        data-visibility={isOpen}
      >
        <div className={styles.overlay} onClick={handleOverlayClick} />
        <div className={styles.modalContent}>
          {modalState ? contentMap[modalState] : null}
        </div>
      </div>
    </Portal>
  );
}

export default CreateYourOwnModal;
export { CreateYourOwnModal, type CreateYourOwnModalProps };
